/**
 * App Component
 *
 * Root component with routing
 */

import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginScreen } from "./components/LoginScreen";
import { AuthCallback } from "./pages/AuthCallback";
import { DashboardLayout } from "./components/DashboardLayout";
import { DashboardHome } from "./pages/DashboardHome";
import { Chat } from "./pages/Chat";
import { Notes } from "./pages/Notes";
import { Links } from "./pages/Links";
import { Feedback } from "./pages/Feedback";
import { FAQ } from "./pages/FAQ";
import { Settings } from "./pages/Settings";
import { Profile } from "./pages/Profile";
import { Pricing } from "./pages/Pricing";
import { isCapacitor } from "./lib/platform";

/**
 * Set up native deep link listener for OAuth callbacks.
 * When running in Capacitor, the OAuth provider redirects to
 * com.duneworks.app://auth/{provider}/callback#token=...
 * The OS routes this back to the app, and we handle it here
 * by navigating to the equivalent /auth/{provider}/callback route
 * with the hash fragment preserved.
 */
function useNativeDeepLinkHandler() {
  useEffect(() => {
    if (!isCapacitor()) return;

    let listenerHandle: any = null;

    import("@capacitor/app").then(({ App }) => {
      listenerHandle = App.addListener("appUrlOpen", async (event) => {
        console.log("[DeepLink] Received URL:", event.url);

        // Close the in-app browser
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.close();
        } catch (e) {
          // Browser may already be closed
        }

        // Parse the deep link URL
        // Format: com.duneworks.app://auth/google/callback#access_token=...&id_token=...
        const url = event.url;

        // Extract the path after the scheme
        const schemeEnd = url.indexOf("://");
        if (schemeEnd === -1) return;

        const afterScheme = url.substring(schemeEnd + 3); // "auth/google/callback#..."
        const hashIndex = afterScheme.indexOf("#");
        const path =
          hashIndex !== -1 ? afterScheme.substring(0, hashIndex) : afterScheme;
        const hash = hashIndex !== -1 ? afterScheme.substring(hashIndex) : "";

        // Only handle OAuth callback URLs
        if (!path.startsWith("auth/")) return;

        // Navigate to the callback route with the hash fragment
        // This re-uses the existing AuthCallback component
        const callbackPath = `/${path}`;
        console.log(
          "[DeepLink] Navigating to:",
          callbackPath,
          "with hash:",
          hash,
        );

        // Update the browser URL so AuthCallback can read window.location.hash
        window.location.hash = hash.startsWith("#") ? hash.substring(1) : hash;
        window.history.replaceState(null, "", callbackPath + hash);

        // Dispatch a popstate event so React Router picks up the change
        window.dispatchEvent(new PopStateEvent("popstate"));
      });
    });

    return () => {
      if (listenerHandle) {
        listenerHandle.then?.((handle: any) => handle.remove());
      }
    };
  }, []);
}

function App() {
  useNativeDeepLinkHandler();

  return (
    <AuthProvider>
      <BrowserRouter basename="/app">
        <Routes>
          <Route path="/welcome" element={<LoginScreen />} />
          <Route path="/auth/google/callback" element={<AuthCallback />} />
          <Route path="/auth/m365/callback" element={<AuthCallback />} />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute skipSubscriptionGate>
                <Pricing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardHome />} />
          </Route>
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Chat />} />
          </Route>
          <Route
            path="/notes"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Notes />} />
          </Route>
          <Route
            path="/links"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Links />} />
          </Route>
          <Route
            path="/feedback"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Feedback />} />
          </Route>
          <Route
            path="/faq"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<FAQ />} />
          </Route>
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Settings />} />
          </Route>
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Profile />} />
          </Route>
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
